import React from 'react';

import { PrimaryEducation } from 'types/collections';

import { UnorderedList } from '@components/Common';
import { TextHeader } from '@components/Onboarding/SchoolOnboarding/components/styles';

/**
 * Capitalize the first letter of a string
 */
export function capitalizeLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Format a date to DD/MM/YYYY
 */
export const formatDate = (date: Date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Highlight matching text in search results with abbreviation support
 */
export const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) return text;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // Create search variations for abbreviation matching
  const searchVariations = [
    normalizedSearchTerm,
    // Add space after periods (Dr.P -> Dr. P)
    normalizedSearchTerm.replace(/\.([a-z])/gi, '. $1'),
    // Remove spaces after periods (Dr. P -> Dr.P)
    normalizedSearchTerm.replace(/\.\s+([a-z])/gi, '.$1'),
  ].filter((v, i, arr) => arr.indexOf(v) === i); // remove duplicates

  // Escape special regex characters and create pattern
  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const patterns = searchVariations.map(escapeRegex).join('|');
  const regex = new RegExp(`(${patterns})`, 'gi');

  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Check if this part matches any of our search variations
    const isMatch = searchVariations.some((variation) => part.toLowerCase() === variation);

    return isMatch ? <TextHeader key={index}>{part}</TextHeader> : part;
  });
};

/**
 * Format Dutch postal code (add space between digits and letters)
 */
const formatPostalCode = (postcode: string): string => {
  const cleanCode = postcode.replace(/\s/g, '');
  // Dutch postal code format: 4 digits + 2 letters
  if (cleanCode.length === 6 && /^\d{4}[A-Za-z]{2}$/.test(cleanCode)) {
    return `${cleanCode.substring(0, 4)} ${cleanCode.substring(4).toUpperCase()}`;
  }

  return postcode;
};

/**
 * Format school address
 */
export const formatAddress = (school: Partial<PrimaryEducation> | null | undefined): string => {
  if (!school) return '';

  const parts = [];

  // Street name and house number addition
  if (school.straatnaam) {
    let addressLine = school.straatnaam;
    if (school.huisnummer_toevoeging) addressLine += ' ' + school.huisnummer_toevoeging;
    parts.push(addressLine);
  }

  // Postal code and city
  const locationParts = [];
  if (school.postcode) {
    const formattedPostcode = formatPostalCode(school.postcode);
    locationParts.push(formattedPostcode);
  }
  if (school.plaatsnaam) {
    // Capitalize first letter, rest lowercase
    const formattedCity = capitalizeLetter(school.plaatsnaam.toLowerCase());
    locationParts.push(formattedCity);
  }

  if (locationParts.length > 0) {
    parts.push(locationParts.join(' '));
  }

  return parts.join(', ');
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone (9 digits after +31, allowing optional leading 0)
 */
export const validatePhone = (phone: string): boolean => {
  const cleanPhone = phone.replace(/\s/g, '');
  // Allow either 9 digits, or a leading 0 followed by 9 digits
  const phoneRegex = /^(0?\d{9})$/;
  return phoneRegex.test(cleanPhone);
};

/**
 * Format phone for saving (add +31 prefix and remove leading 0 if present)
 */
export const formatPhoneForSaving = (phone: string): string => {
  const cleanPhone = phone.replace(/\s/g, '');
  // Remove leading 0 if present before adding +31
  return `+31${cleanPhone.replace(/^0/, '')}`;
};

/**
 * Strip +31 prefix when loading existing phone data
 */
export const stripPhonePrefix = (phone: string): string => {
  if (phone.startsWith('+31')) {
    return phone.substring(3);
  }
  return phone;
};

/**
 * Format phone number for SMS sending (E.164 format)
 */
export const formatPhoneForSMS = (phone: string): string | null => {
  if (!phone) return null;

  const cleanPhone = phone.replace(/\s/g, '');
  // If already in E.164 format (+31...), validate the Dutch part
  if (cleanPhone.startsWith('+31')) {
    const dutchPart = cleanPhone.substring(3);
    if (validatePhone(dutchPart)) {
      return cleanPhone; // Already valid E.164
    }
    return null;
  }

  // If just the Dutch number, validate and add +31
  if (validatePhone(cleanPhone)) {
    // Remove leading 0 if present and add +31
    const normalizedDutch = cleanPhone.startsWith('0') ? cleanPhone.substring(1) : cleanPhone;
    const e164 = `+31${normalizedDutch}`;
    return e164;
  }
  return null;
};

/**
 * Format the text with bullet points and bold text
 * Supports: • for bullet points, **text** for bold
 */
export const renderParagraph = (text: string | null): string | React.ReactNode => {
  if (!text) return text;

  // Handle bullet points
  if (text.includes('•')) {
    // Split the string into an array of list items
    const listItems = text.split('•').filter((item) => item.trim() !== '');

    return (
      <UnorderedList>
        {listItems.map((item, index) => (
          <li key={index}>{item.trim()}</li>
        ))}
      </UnorderedList>
    );
  }

  // Handle bold text with **text** markers
  if (text.includes('**')) {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return (
      <>
        {parts.map((part, index) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            // Remove the ** markers and wrap in <strong>
            const boldText = part.slice(2, -2);
            return <strong key={index}>{boldText}</strong>;
          }
          return <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </>
    );
  }

  return text;
};
